import { useState, useRef, useCallback, useEffect } from 'react';

export function useCameraStream() {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setIsPaused(false);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 360 },
          aspectRatio: { ideal: 16 / 9 },
          facingMode: 'user',
        },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }

      setStream(mediaStream);
      setIsStreaming(true);
      setIsPaused(false);
    } catch (err) {
      console.error('[Camera] 웹캠 권한 또는 시작 오류:', err);
      setCameraError(
        err.name === 'NotAllowedError'
          ? '카메라 접근 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.'
          : '카메라 장치를 시작할 수 없습니다. 연결 상태를 확인해주세요.'
      );
      setIsStreaming(false);
      setIsPaused(false);
    }
  }, []);

  const togglePauseCamera = useCallback(() => {
    if (!videoRef.current || !isStreaming) return;

    if (isPaused) {
      videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
  }, [isPaused, isStreaming]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    setIsStreaming(false);
    setIsPaused(false);
  }, [stream]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return {
    videoRef,
    stream,
    isStreaming,
    isPaused,
    cameraError,
    startCamera,
    stopCamera,
    togglePauseCamera,
  };
}
